import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { startupsApi } from '@/services/api'
import { FormSubmission } from '../submission/flows/FormSubmission'
import { Loader2 } from 'lucide-react'
import { StartupSubmissionForm } from '@/types'

export function StartupEditPage() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()

    const { data: startup, isLoading, error } = useQuery({
        queryKey: ['startup', id],
        queryFn: () => startupsApi.get(id!),
        enabled: !!id,
    })

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <Loader2 className="w-12 h-12 animate-spin text-primary" />
            </div>
        )
    }

    if (error || !startup) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen gap-4">
                <h1 className="text-2xl font-bold text-destructive">Error loading startup</h1>
                <p className="text-muted-foreground">{error ? (error as Error).message : 'Startup not found'}</p>
                <button onClick={() => navigate('/startups')} className="text-primary hover:underline">
                    Back to Startups
                </button>
            </div>
        )
    }

    // Map StartupSubmission to StartupSubmissionForm
    // The backend now guarantees startup.profile follows the nested structure
    const initialData: StartupSubmissionForm = {
        company_info: {
            ...startup.profile.company_info,
            // Ensure metadata is nested inside company_info for the form
            metadata: startup.profile.company_info?.metadata || {}
        },
        document_uploads: [], // For new files
        documents: (startup.profile.documents || []).map(doc => ({
            filename: doc.filename,
            content_type: doc.mime_type || "application/octet-stream",
            size: doc.file_size,
            gcs_path: doc.storage_path
        })),
    } as any

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12">
            <FormSubmission
                onBack={() => navigate(`/startups/${id}`)}
                initialData={initialData}
                mode="edit"
                existingStartupId={id}
            />
        </div>
    )
}
